import React from 'react';
import { Button } from '@/components/ui/button';
import { useQuickscan } from '@/contexts/QuickscanContext';
import { ArrowRight, Building, MapPin, Zap, Target } from 'lucide-react';
import { NewLocationSubScreen } from './sub/NewLocationSubScreen';
import { SustainabilitySubScreen } from './sub/SustainabilitySubScreen';
import { BusinessGrowthSubScreen } from './sub/BusinessGrowthSubScreen';
import { NewEquipmentSubScreen } from './sub/NewEquipmentSubScreen';

const expansionPlans = [
  { id: 'growth', label: 'Bedrijfsgroei', icon: Building, description: 'Meer werknemers of grotere ruimte' },
  { id: 'location', label: 'Nieuwe locatie', icon: MapPin, description: 'Extra vestiging of verhuizing' },
  { id: 'equipment', label: 'Nieuwe apparatuur', icon: Zap, description: 'Machines, servers of andere uitrusting' },
  { id: 'sustainability', label: 'Duurzaamheidsdoelen', icon: Target, description: 'CO2-neutraal of groene certificering' },
];

export const ExpansionPlansScreen = () => {
  const { data, updateData, nextStep, prevStep, activeExpansionPlan, subStep } = useQuickscan();

  // Show sub-screen if we're in a sub-step
  if (subStep && activeExpansionPlan) {
    switch (activeExpansionPlan) {
      case 'location':
        return <NewLocationSubScreen />;
      case 'sustainability':
        return <SustainabilitySubScreen />;
      case 'growth':
        return <BusinessGrowthSubScreen />;
      case 'equipment':
        return <NewEquipmentSubScreen />;
      default:
        break;
    }
  }

  const togglePlan = (planId: string) => {
    const currentPlans = [...data.expansionPlans];
    const index = currentPlans.indexOf(planId);
    
    if (index > -1) {
      currentPlans.splice(index, 1);
      // Also remove details if plan is deselected
      const newDetails = { ...data.expansionDetails };
      delete newDetails[planId];
      updateData('expansionDetails', newDetails);
    } else {
      currentPlans.push(planId);
    }
    
    updateData('expansionPlans', currentPlans);
  };

  const getCompletionStatus = (planId: string) => {
    return data.expansionDetails[planId] ? '✓ Ingevuld' : 'Nog in te vullen';
  };

  const getCompletionColor = (planId: string) => {
    return data.expansionDetails[planId] ? 'text-energy-green' : 'text-gray-500';
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-energy-green mb-2">
          Wat zijn je plannen voor de komende 2 jaar?
        </h2>
        <p className="text-gray-600">
          Dit helpt ons om je toekomstige energiebehoeften in te schatten
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {expansionPlans.map((plan) => {
          const Icon = plan.icon;
          const isSelected = data.expansionPlans.includes(plan.id);
          
          return (
            <div
              key={plan.id}
              onClick={() => togglePlan(plan.id)}
              className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                isSelected 
                  ? 'border-energy-blue bg-energy-blue/5 shadow-md animate-glow' 
                  : 'border-gray-200 hover:border-energy-blue/50 hover:shadow-sm'
              }`}
            >
              <Icon className={`w-10 h-10 mb-4 ${
                isSelected ? 'text-energy-blue' : 'text-gray-600'
              }`} />
              
              <h3 className="text-lg font-semibold mb-2">{plan.label}</h3>
              <p className="text-sm text-gray-600 mb-3">{plan.description}</p>
              
              {isSelected && (
                <div className={`text-sm font-medium ${getCompletionColor(plan.id)}`}>
                  {getCompletionStatus(plan.id)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
        <div className="text-center">
          <h3 className="font-semibold mb-2">
            {data.expansionPlans.length === 0 
              ? 'Geen concrete plannen? Dat is prima!'
              : `${data.expansionPlans.length} plan(nen) geselecteerd`
            }
          </h3>
          <p className="text-sm text-gray-600">
            {data.expansionPlans.length === 0 
              ? 'We berekenen je huidige situatie en tonen mogelijkheden voor de toekomst.'
              : 'Klik op "Volgende" om details per plan in te vullen voor betere aanbevelingen.'
            }
          </p>
        </div>
      </div>

      <div className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={prevStep}
          className="border-gray-300"
        >
          Vorige
        </Button>
        
        <Button 
          onClick={nextStep}
          className="bg-[#014336] text-white hover:bg-[#014336]/90"
        >
          Volgende <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};
